import pdfMake from "pdfmake/build/pdfmake";
import pdfFonts from "pdfmake/build/vfs_fonts";
import { fontRegular } from "@utils/pdfmake/fontRegular";
import { TDocumentDefinitions } from "pdfmake/interfaces";
import { docVersionKey, formatToDecimal, getApprovalAuthority, procurementProcess } from "~/Utils";
import { logo } from "./logo";
import { ePodLogo } from "./epodLogo";
import { pdfLogo } from "./pdfLogo";
import { capitalize, get } from "lodash";
import { getFormattedDate } from "../CommonUtils";
import htmlToPdfmake from "html-to-pdfmake";
pdfMake.vfs = pdfFonts.pdfMake.vfs;

export const printPdf = (data: any, type: string) => {
  let reviewers = data.reviewers;

  const budgetManagers =
    reviewers &&
    reviewers.filter((reviewer: any) =>
      ["Senior Manager", "Manager", "APC Secretariat", "Senior Executive", "Executive", "Officer"].includes(
        reviewer.designation
      )
    );

  const renderHtmlContent: any = (html: string) => {
    if (!html) return [{ text: "", style: "fieldValue" }];

    const content = htmlToPdfmake(html, {
      defaultStyles: {
        p: { fontSize: 11, marginBottom: 6 },
        li: { fontSize: 11 },
        table: { marginBottom: 10 },
        th: { bold: true, fillColor: "#f2f2f2", fontSize: 7, noWrap: false },
        td: { fontSize: 7, noWrap: false },
      },
    });

    const PAGE_WIDTH = 555;

    const fixTableWidths = (item: any): any => {
      if (!item || typeof item !== "object") return item;

      if (item.table) {
        const body: any[][] = Array.isArray(item.table.body) ? item.table.body : [];
        const columnCount = body.reduce((max: number, row: any[]) =>
          Array.isArray(row) ? Math.max(max, row.length) : max, 1
        ) || 1;

        const PAGE_WIDTH = 555;
        const MIN_COL_WIDTH = 15;
        const MIN_FONT_SIZE = 5;
        const MIN_PADDING = 1;

        const dynamicPadding = Math.max(MIN_PADDING, columnCount > 20 ? 1 : columnCount > 15 ? 1.5 : 2);
        const totalPadding = columnCount * dynamicPadding * 2;
        const availableWidth = PAGE_WIDTH - totalPadding;
        const idealColWidth = Math.floor(availableWidth / columnCount);
        const colWidth = Math.max(MIN_COL_WIDTH, idealColWidth);

        const idealFontSize = columnCount > 20 ? 5 : columnCount > 15 ? 6 : columnCount > 10 ? 7 : 8;
        const dynamicFontSize = Math.max(MIN_FONT_SIZE, idealFontSize);

        const totalTableWidth = colWidth * columnCount + totalPadding;

        item.table.widths = Array(columnCount).fill(colWidth);
        item.table.dontBreakRows = false;

        item.layout = {
          hLineWidth: () => 0.3,
          vLineWidth: () => 0.3,
          hLineColor: () => "#aaa",
          vLineColor: () => "#aaa",
          paddingLeft: () => dynamicPadding,
          paddingRight: () => dynamicPadding,
          paddingTop: () => dynamicPadding,
          paddingBottom: () => dynamicPadding,
        };

        item.table.body = body.map((row: any[]) => {
          if (!Array.isArray(row)) return row;
          const padded = row.length < columnCount
            ? [...row, ...Array(columnCount - row.length).fill({})]
            : row;
          return padded.map((cell: any) => {
            if (cell === null || cell === undefined) return {};
            if (Array.isArray(cell)) return cell.map(fixTableWidths);
            if (typeof cell === "object") {
              const hasPdfContent =
                cell.text !== undefined ||
                cell.stack !== undefined ||
                cell.table !== undefined ||
                cell.image !== undefined ||
                cell.columns !== undefined ||
                cell.ul !== undefined ||
                cell.ol !== undefined ||
                cell.qr !== undefined;
              if (hasPdfContent) {
                cell.fontSize = dynamicFontSize;
              }
              if (cell.stack) cell.stack = cell.stack.map(fixTableWidths);
              if (cell.text && typeof cell.text !== "string") {
                cell.text = fixTableWidths(cell.text);
              }
            }
            return cell;
          });
        });
      }

      if (item.stack) item.stack = item.stack.map((i: any) => fixTableWidths(i));
      if (item.columns) item.columns = item.columns.map((i: any) => fixTableWidths(i));

      return item;
    };

    const safeContent = Array.isArray(content) ? content : [content];
    if (safeContent.length === 0) {
      return [{ text: "", style: "fieldValue" }];
    }

    return safeContent.map(fixTableWidths);
  };

  const approvers =
    reviewers &&
    reviewers.filter((reviewer: any) => {
      const excluded = [
        "Senior Manager",
        "Manager",
        "APC Secretariat",
        "One Stop Centre",
        "One Stop Centre, PCD",
        "Secretariat, PCD",
        "Secratariat, PCD",
        "Executive",
        "Senior Executive",
        "Officer",
      ];
      const isExcluded = excluded.includes(reviewer.designation);
      const hasNoDesignation = !reviewer.designation;
      return reviewer.designation && !excluded.includes(reviewer.designation);
    });
  const oneStopApprovers =
    reviewers &&
    reviewers.filter(
      (reviewer: any) =>
        reviewer.designation === "Secretariat, PCD" ||
        reviewer.designation === "Secratariat, PCD" ||
        reviewer.designation === "One Stop Centre, PCD" ||
        reviewer.designation === "One Stop Centre"
    );

  const getStatus = (statusVal: string) => {

    let status = statusVal === "completed" ? "approved" : statusVal;

    if (status === "pending") {
      status = "-";
    } else {
      status = capitalize(status);
    }
    return status;
  };

  const getBudgetManagers = () => {
    if (!budgetManagers || budgetManagers.length === 0) {
      return [
        [{ text: "No Budget Manager/APC Secretariat Details Found", style: "tableContent", colSpan: 6, alignment: "center" }, {}, {}, {}, {}, {}],
      ];
    }
    return budgetManagers.map((manager: any) => {
      return [
        { text: manager.designation, style: "tableContent" },
        { text: manager.name, style: "tableContent" },
        { text: manager.emailAddress, style: "tableContent" },
        // { text: manager.status ? "Approved" : "Pending", style: "tableContent", color : manager.status ? "#4ccb83" : "#000" },
        { text: getStatus(manager.status), style: "tableContent" },
        { text: getFormattedDate(manager.reviewedAt), style: "tableContent" },
        { text: manager.division, style: "tableContent" },
      ];
    });
  };

  const formatVersion = (value: any) => {
    if (value === null || value === undefined) return "-";
    try {
      const s = String(value);
      if (!s.includes(".")) {
        return `V${Number(s)}`;
      }
      const parts = s.split(".").map(Number);
      const major = parts[0] || 0;
      const minor = parts[1] || 0;
      const version = (major - 1) * 10 + minor;
      return `V${version}`;
    } catch (e) {
      return String(value);
    }
  };

  const getApprovers = () => {

    if (!approvers || approvers.length === 0) {
      return [];
    }

    return approvers.map((approver: any, index: number) => {

      const resolvedStatus = getStatus(approver?.status);

      const row = [
        { text: approver.designation, style: "tableContent" },
        { text: approver.name, style: "tableContent" },
        { text: approver.emailAddress, style: "tableContent" },
        { text: resolvedStatus, style: "tableContent" },
        { text: getFormattedDate(approver.reviewedAt), style: "tableContent" },
      ];
      return row;
    });
  };

  const getOneStopCentreApprovers = () => {
    if (oneStopApprovers && oneStopApprovers.length > 0) {
      return oneStopApprovers.map((approver: any) => {
        return [
          { text: approver.designation, style: "tableContent" },
          { text: approver.name, style: "tableContent" },
          { text: approver.emailAddress, style: "tableContent" },
          { text: getStatus(approver.status), style: "tableContent" },
          // { text: approver.status ? "Approved" : "Pending", style: "tableContent", color : approver.status ? "#4ccb83" : "#000" },
          { text: getFormattedDate(approver.reviewedAt), style: "tableContent" },
        ];
      });
    }
    return [
      [{ text: "No Secretariat, PCD Approvers Details Found", style: "tableContent", colSpan: 5, alignment: "center" }, {}, {}, {}, {}],
    ];
  };

  const getWatchers = () => {
    if (!data.watchers || data.watchers.length === 0) {
      return [[{ text: "No Watchers Found", style: "tableContent", colSpan: 4, alignment: "center" }, {}, {}, {}]];
    }
    return data.watchers.map((watcher: any) => {
      return [
        { text: watcher.role === "PCD" ? "PCD" : watcher.role, style: "tableContent" },
        { text: watcher.name, style: "tableContent" },
        { text: watcher.emailAddress, style: "tableContent" },
        { text: getFormattedDate(watcher.sentAt), style: "tableContent" },
      ];
    });
  };

  const getComments = () => {
    if (data.comments && data.comments.length > 0) {
      return data.comments.map((comment: any) => {
        return [
          { text: comment.reviewerDesignation, style: "tableContent" },
          { text: comment.reviewerName, style: "tableContent" },
          { text: comment.commentedBy, style: "tableContent" },
          { text: comment.documentName, style: "tableContent" },
          { text: formatVersion(comment.versionOrder), style: "tableContent" },
          { text: comment.message, style: "tableContent" },
          { text: getFormattedDate(comment.commentedOn), style: "tableContent" },
        ];
      });
    }
    return [[{ text: "No comments found", style: "tableContent", colSpan: 7, alignment: "center" }, {}, {}, {}, {}, {}, {}]];
  };
  const getDocVersions = () => {
    const records = data?.docVersion?.records;
    if (!records || records.length === 0) {
      return [[{ text: "No Version Records Found", style: "tableContent", colSpan: 2, alignment: "center" }, {}]];
    }
    const filtered = records.filter((doc: any) => {
      if (doc.order === 0) return false;
      const formatted = formatVersion(doc.docVersion);
      if (formatted === null) return false;
      return true;
    });
    if (filtered.length === 0) {
      return [[{ text: "No Version Records Found", style: "tableContent", colSpan: 2, alignment: "center" }, {}]];
    }
    return filtered.map((doc: any) => {
      return [
        { text: formatVersion(doc.docVersion), style: "tableContent" },
        { text: getFormattedDate(doc.createdOn), style: "tableContent" },
      ];
    });
  };
  const getLogs = () => {
    if (!data.logs || data.logs.length === 0) {
      return [[{ text: "No Logs Found", style: "tableContent", colSpan: 4, alignment: "center" }, {}, {}, {}]];
    }
    return data.logs.map((log: any) => {
      let decoded = log.detail || "";
      try {
        decoded = decodeURIComponent(log.detail);
      } catch {
        decoded = log.detail;
      }
      const formattedDetail = log.detail
        ? decoded.replace(/\b(\d+)\.(\d+)\b/g, (_: string, major: string, minor: string) => {
          const version = (Number(major) - 1) * 10 + Number(minor);
          return version === 0 ? `${major}.${minor}` : `V${version}`;
        })
        : "-";
      return [
        { text: log.action, style: "tableContent" },
        { text: formattedDetail, style: "tableContent" },
        { text: log.actionPerformedBy, style: "tableContent" },
        { text: getFormattedDate(log.actionPerformedOn), style: "tableContent" },
      ];
    });
  };

  const getProjectDocuments = () => {
    const safeDocuments = Array.isArray(data.documents) ? data.documents : [];
    const overallPdfRegex = /^SPA\d*\(V\d+\)\.pdf$/;
    const sortedData = safeDocuments
      ?.filter((doc: any) => !overallPdfRegex.test(doc.documentName))
      ?.sort((a: any, b: any) => Number(a.documentId) - Number(b.documentId));
    return sortedData.map((doc: any) => {
      return {
        columns: [
          {
            image: pdfLogo,
            alignment: "justify",
            fit: [30, 20],
            width: 30,
          },
          {
            text: doc.documentName,
            link: `${doc.blobUrl}${docVersionKey}`,
            style: "fieldValue",
          },
        ],
        marginBottom: 12,
      };
    });
  };

  const getAdditionalDocumentLinks = () => {
    return (
      (data.documentLinkDetails &&
        data.documentLinkDetails.map((link: any) => {
          return {
            columns: [
              {
                text: link.name,
                fontSize: 11,
                color: "#29256E",
              },
              {
                text: link.url,
                link: decodeURIComponent(link?.url) || encodeURIComponent(link?.url),
                fontSize: 11,
                color: "#29256E",
                decoration: "underline",
              },
            ],
            marginBottom: 12,
          };
        })) ||
      []
    );
  };

  let statusRowCols: any = [
    {
      width: "40%",
      stack: [
        {
          text: "Status",
          style: "fieldLabel",
        },
        {
          text: capitalize(get(data, "status")),
          style: "fieldValue",
        },
      ],
    },
  ];
  if (data.status === "rejected") {
    statusRowCols.push({
      width: "60%",
      stack: [
        {
          text: "Reason for Rejection",
          style: "fieldLabel",
        },
        {
          text: get(data, "rejectReason"),
          style: "fieldValue",
        },
      ],
    });
  }
  if (data.status === "cancelled") {
    statusRowCols.push({
      width: "60%",
      stack: [
        {
          text: "Reason for Cancellation",
          style: "fieldLabel",
        },
        {
          text: get(data, "cancelReason"),
          style: "fieldValue",
        },
      ],
    });
  }

  var docDefinition: TDocumentDefinitions = {
    pageSize: "A4",
    pageOrientation: "portrait",
    pageMargins: [20, 90, 20, 30],
    header: function (currentPage: any, pageCount: any, pageSize: any) {
      return {
        columns: [
          {
            text: "", // Empty column to center the images
            width: "*",
          },
          {
            image: logo,
            width: 100,
            alignment: "center",
            // fit: [100, 80],
            margin: [0, 0, 10, 0], // Right margin
          },
          {
            image: ePodLogo,
            width: 120,
            alignment: "center",
            // fit: [140, 40],
            margin: [0, 20, 0, 0], // Left margin
          },
          {
            text: "", // Empty column to center the images
            width: "*",
          },
        ],
        margin: [0, 10], // Margin for the header
        alignment: "center",
      };
    },
    footer: function (currentPage: any, pageCount: any) {
      return [{ text: currentPage.toString() + " of " + pageCount, alignment: "right", marginRight: 40 }];
    },
    content: [
      {
        text: "Project Information",
        marginBottom: 16,
        style: "pageTitle",
      },
      {
        columns: [
          {
            width: "100%",
            stack: [
              {
                text: "Project ID",
                style: "fieldLabel",
              },
              {
                text: get(data, "projectId"),
                style: "fieldValue",
              },
            ],
          },
        ],
        marginBottom: 16,
      },
      {
        columns: [
          {
            width: "100%",
            stack: [
              {
                text: "Project Name",
                style: "fieldLabel",
              },
              {
                text: get(data, "projectTitle"),
                style: "fieldValue",
              },
            ],
          },
        ],
        marginBottom: 16,
      },
      {
        columns: [
          {
            width: "100%",
            stack: [
              {
                text: "Budget",
                style: "fieldLabel",
              },
              {
                text: get(data, "budget", "Uncategorized") || "Uncategorized",
                style: "fieldValue",
              },
            ],
          },
        ],
        marginBottom: 16,
      },
      {
        columns: [
          {
            width: "100%",
            stack: [
              {
                text: "SPA Types",
                style: "fieldLabel",
              },
              {
                text: get(data, "awardAndStrategyPaper", "Uncategorized") || "Uncategorized",
                style: "fieldValue",
              },
            ],
          },
        ],
        marginBottom: 16,
      },
      {
        columns: [
          {
            width: "100%",
            stack: [
              {
                text: "Project Summary",
                style: "fieldLabel",
              },
              {
                text: get(data, "projectSummary"),
                style: "fieldValue",
              },
            ],
          },
        ],
        marginBottom: 16,
      },
      {
        columns: [
          {
            width: "40%",
            stack: [
              {
                text: "Budget Amount/Pre-Tender Estimate (PTE)",
                style: "fieldLabel",
              },
              {
                text: formatToDecimal(data?.currency?.totalCost, get(data, "currency.currencyCode", "MYR")) || "-",
                style: "fieldValue",
              },
            ],
          },
          {
            width: "30%",
            stack: [
              {
                text: "Subsidiary/Division",
                style: "fieldLabel",
              },
              {
                text: get(data, "subsidiary"),
                style: "fieldValue",
              },
            ],
          },
          {
            width: "30%",
            stack: [
              {
                text: "Cost Centre",
                style: "fieldLabel",
              },
              {
                text: get(data, "division"),
                style: "fieldValue",
              },
            ],
          },
        ],
        marginBottom: 16,
      },
      {
        columns: [
          {
            width: "40%",
            stack: [
              {
                text: "Approval Authority",
                style: "fieldLabel",
              },
              {
                text: getApprovalAuthority(get(data, "approvalAuthority")),
                style: "fieldValue",
              },
            ],
          },
        ],
      },
      {
        marginTop: 16,
        columns: statusRowCols,
      },
      {
        stack: ["A. Information on Procurement Process \n"],
        style: "fieldLabel",
        marginBottom: 8,
        marginTop: 16,
        alignment: "justify",
      },
      {
        stack: [get(data, "procurementNumber") + ". " + procurementProcess[get(data, "procurementNumber")]],
        style: "fieldValue",
        alignment: "justify",
      },
      {
        stack: ["B. Summary of the Procurement (e.g Background, Evaluation & Justification)"],
        style: "fieldLabel",
        marginBottom: 8,
        marginTop: 16,
      },
      {
        stack: (() => {
          const result = renderHtmlContent(get(data, "procurementSummary"));
          if (!result || result === "" || (Array.isArray(result) && result.length === 0)) {
            return [{ text: "", style: "fieldValue" }];
          }
          return Array.isArray(result) ? result : [result];
        })(),
      },
      {
        stack: [
          {
            text: "C. Recommendation",
            style: "fieldLabel",
            marginBottom: 8,
            marginTop: 16,
          },
          {
            text: "Recommended Bidder/Vendor",
            style: "fieldLabel",
            marginBottom: 8,
          },
          {
            text: get(data, "recommendedVendor"),
            style: "fieldValue",
            marginBottom: 8,
          },
          {
            stack: [
              {
                text: "Proposed Contract Value",
                style: "fieldLabel",
              },
              {
                text: get(data, "contractValue"),
                style: "fieldValue",
                marginBottom: 8,
              },
            ],
          },
          {
            stack: [
              {
                text: "Proposed Contract Period",
                style: "fieldLabel",
              },
              {
                text: get(data, "contractPeriod"),
                style: "fieldValue",
              },
            ],
          },
        ],
      },
      {
        marginTop: 20,
        stack: [
          {
            text: "Project Documents",
            style: "pageTitle",
            marginBottom: 16,
          },
          ...getProjectDocuments(),
        ],
      },
      {
        stack: [
          { text: "Additional Document Links", style: "pageTitle", marginBottom: 16 },
          ...getAdditionalDocumentLinks(),
        ],
      },
      {
        text: " Budget Manager/APC Secretariat Details",
        style: "pageTitle",
        marginBottom: 4,
        marginTop: 20,
      },
      {
        style: "tableExample",
        table: {
          widths: ["17%", "15%", "25%", "12%", "16%", "15%"],
          headerRows: 1,
          body: [
            [
              { text: "Designation", style: "tableHeader" },
              { text: "Name", style: "tableHeader" },
              { text: "Email Address", style: "tableHeader" },
              { text: "Status", style: "tableHeader" },
              { text: "Reviewed At", style: "tableHeader" },
              { text: "Cost Centre", style: "tableHeader" },
            ],
            ...getBudgetManagers(),
          ],
        },
        // layout: "headerLineOnly",
        layout: {
          hLineWidth: function (i: any, node: any) {
            return i === 1 ? 0.5 : 0;
          },
          vLineWidth: function (i: any, node: any) {
            return 0;
          },
          hLineColor: function (i: any, node: any) {
            return i === 0 || i === node.table.body.length ? "black" : "grey";
          },
        },
      },
      {
        text: "Secretariat, PCD Approvers Details",
        style: "pageTitle",
        marginBottom: 4,
        marginTop: 20,
      },
      {
        table: {
          headerRows: 1,
          widths: ["25%", "20%", "25%", "14%", "16%"],
          body: [
            [
              { text: "Designation", style: "tableHeader" },
              { text: "Name", style: "tableHeader" },
              { text: "Email Address", style: "tableHeader" },
              { text: "Status", style: "tableHeader" },
              { text: "Reviewed At", style: "tableHeader" },
            ],
            ...getOneStopCentreApprovers(),
          ],
        },
        layout: {
          hLineWidth: function (i: any, node: any) {
            return i === 1 ? 0.5 : 0;
          },
          vLineWidth: function (i: any, node: any) {
            return 0;
          },
          hLineColor: function (i: any, node: any) {
            return i === 0 || i === node.table.body.length ? "black" : "grey";
          },
        },
      },
      {
        text: "Approvers Details",
        style: "pageTitle",
        marginBottom: 4,
        marginTop: 20,
      },
      {
        style: "tableExample",
        table: {
          widths: ["20%", "20%", "25%", "15%", "20%"],
          headerRows: 1,
          body: [
            [
              { text: "Designation", style: "tableHeader" },
              { text: "Name", style: "tableHeader" },
              { text: "Email Address", style: "tableHeader" },
              { text: "Status", style: "tableHeader" },
              { text: "Reviewed At", style: "tableHeader" },
            ],
            ...getApprovers(),
          ],
        },
        // layout: "headerLineOnly",
        layout: {
          hLineWidth: function (i: any, node: any) {
            return i === 1 ? 0.5 : 0;
          },
          vLineWidth: function (i: any, node: any) {
            return 0;
          },
          hLineColor: function (i: any, node: any) {
            return i === 0 || i === node.table.body.length ? "black" : "grey";
          },
          // hLineStyle: function (i, node) { return {dash: { length: 10, space: 4 }}; },
          // vLineStyle: function (i, node) { return {dash: { length: 10, space: 4 }}; },
          // paddingLeft: function(i, node) { return 4; },
          // paddingRight: function(i, node) { return 4; },
          // paddingTop: function(i, node) { return 2; },
          // paddingBottom: function(i, node) { return 2; },
          // fillColor: function (rowIndex, node, columnIndex) { return null; }
        },
      },

      {
        text: "Watchers Details",
        style: "pageTitle",
        marginBottom: 4,
        marginTop: 20,
      },
      {
        style: "tableExample",
        table: {
          widths: ["23%", "22%", "25%", "30%"],
          headerRows: 1,
          body: [
            [
              { text: "Department / Role", style: "tableHeader" },
              { text: "Name", style: "tableHeader" },
              { text: "Email Address", style: "tableHeader" },
              { text: "Sent At", style: "tableHeader" },
            ],
            ...getWatchers(),
          ],
        },
        // layout: "headerLineOnly",
        layout: {
          hLineWidth: function (i: any, node: any) {
            return i === 1 ? 0.5 : 0;
          },
          vLineWidth: function (i: any, node: any) {
            return 0;
          },
          hLineColor: function (i: any, node: any) {
            return i === 0 || i === node.table.body.length ? "black" : "grey";
          },
        },
      },
      {
        text: "Comments",
        style: "pageTitle",
        marginBottom: 4,
        marginTop: 20,
      },
      {
        style: "tableExample",
        table: {
          widths: ["10%", "15%", "20%", "12%", "8%", "20%", "15%"],
          headerRows: 1,
          body: [
            [
              { text: "Designation", style: "tableHeader" },
              { text: "Name", style: "tableHeader" },
              { text: "Email", style: "tableHeader" },
              { text: "Doc Name", style: "tableHeader" },
              { text: "Version", style: "tableHeader" },
              { text: "Comments", style: "tableHeader" },
              { text: "Commented At", style: "tableHeader" },
            ],
            ...getComments(),
          ],
        },
        // layout: "headerLineOnly",
        layout: {
          hLineWidth: function (i: any, node: any) {
            return i === 1 ? 0.5 : 0;
          },
          vLineWidth: function (i: any, node: any) {
            return 0;
          },
          hLineColor: function (i: any, node: any) {
            return i === 0 || i === node.table.body.length ? "black" : "grey";
          },
        },
      },
      {
        text: "SPA Versions",
        style: "pageTitle",
        marginBottom: 4,
        marginTop: 20,
      },
      {
        style: "tableExample",
        table: {
          widths: ["30%", "70%"],
          headerRows: 1,
          body: [
            [
              { text: "Version", style: "tableHeader" },
              { text: "Last Updated", style: "tableHeader" },
            ],
            ...getDocVersions(),
          ],
        },
        // layout: "headerLineOnly",
        layout: {
          hLineWidth: function (i: any, node: any) {
            return i === 1 ? 0.5 : 0;
          },
          vLineWidth: function (i: any, node: any) {
            return 0;
          },
          hLineColor: function (i: any, node: any) {
            return i === 0 || i === node.table.body.length ? "black" : "grey";
          },
        },
      },
      {
        text: "Logs",
        style: "pageTitle",
        marginBottom: 4,
        marginTop: 20,
      },
      {
        style: "tableExample",
        table: {
          widths: ["20%", "40%", "20%", "20%"],
          headerRows: 1,
          body: [
            [
              { text: "Action", style: "tableHeader" },
              { text: "Details", style: "tableHeader" },
              { text: "Performed By", style: "tableHeader" },
              { text: "Performed At", style: "tableHeader" },
            ],
            ...getLogs(),
          ],
        },
        // layout: "headerLineOnly",
        layout: {
          hLineWidth: function (i: any, node: any) {
            return i === 1 ? 0.5 : 0;
          },
          vLineWidth: function (i: any, node: any) {
            return 0;
          },
          hLineColor: function (i: any, node: any) {
            return i === 0 || i === node.table.body.length ? "black" : "grey";
          },
        },
      },
    ],
    styles: {
      header: {
        fontSize: 18,
        // bold: true,
        alignment: "justify",
      },
      headerContent: {
        alignment: "center",
      },
      pageTitle: {
        fontSize: 14,
        color: "#29256E",
        bold: true,
      },
      fieldLabel: {
        fontSize: 12,
        color: "#29256E",
        marginBottom: 4,
        // bold: true
      },
      fieldValue: {
        fontSize: 11,
        color: "#000",
        // bold: true
      },
      tableHeader: {
        margin: 4,
        fontSize: 12,
        color: "#29256E",
      },
      tableContent: {
        margin: 4,
        fontSize: 11,
      },
    },
  };
  return docDefinition;
};
